import React, { useState } from 'react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import { Button, ButtonProps } from 'ui/button/Button';
import { Fieldset, Flex } from './ButtonGroup.styles';
import { BUTTON_GROUP_TYPES } from './types';

type ButtonGroupOption = Omit<ButtonProps, 'size' | 'variant'> & {
  id: string;
  'data-test'?: string;
};

export type Props = {
  /** Description of the button grouping for screen-readers; See https://www.w3.org/WAI/tutorials/forms/grouping */
  description?: string;
  size?: BUTTON_SIZES;
  variant?: BUTTON_VARIANTS;
  options: ButtonGroupOption[];
  isDisabled?: boolean;
  defaultSelectedIds?: string[];
  type?: BUTTON_GROUP_TYPES;
};

const getNextIds = (currentIds: string[], newId: string) =>
  currentIds.includes(newId)
    ? currentIds.filter((id: string) => id !== newId)
    : [...currentIds, newId];

const getNextId = (currentIds: string[], newId: string) =>
  currentIds.includes(newId) ? [] : [newId];

export const ButtonGroup = React.forwardRef(
  (
    {
      description,
      isDisabled = false,
      options,
      size = BUTTON_SIZES.MEDIUM,
      variant = BUTTON_VARIANTS.SECONDARY,
      defaultSelectedIds = [],
      type = BUTTON_GROUP_TYPES.DEFAULT,
      ...props
    }: Props,
    ref: React.ForwardedRef<HTMLFieldSetElement>,
  ) => {
    const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);

    const handleClick = (optionId: string) => {
      if (type === BUTTON_GROUP_TYPES.DEFAULT) return;
      const getNext = type === BUTTON_GROUP_TYPES.MULTI_SELECT ? getNextIds : getNextId;
      const next = getNext(selectedIds, optionId);
      setSelectedIds(next);
    };

    return (
      <Fieldset aria-disabled={isDisabled} disabled={isDisabled} ref={ref} {...props}>
        <VisuallyHidden>
          <legend>{description}</legend>
        </VisuallyHidden>
        <Flex data-variant={variant} data-type={type}>
          {options.map((option) => (
            <Button
              key={option.id}
              data-test={option['data-test']}
              isDisabled={isDisabled || option.isDisabled}
              size={size}
              variant={variant}
              aria-pressed={selectedIds.includes(option.id)}
              onClick={(e) => {
                option.onClick?.(e);
                handleClick(option.id);
              }}
            >
              {option.children}
            </Button>
          ))}
        </Flex>
      </Fieldset>
    );
  },
);
